import { useQuery } from "@apollo/client";
import Add from "@mui/icons-material/Add";
import {
  Box,
  Chip,
  ClickAwayListener,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  Popper,
  Stack,
  Tooltip,
  useAutocomplete,
  useTheme,
} from "@mui/material";
import { gql } from "generated-graphql";
import { TagType } from "generated-graphql/graphql";
import { useTenant } from "hooks/useTenant";
import { omit } from "lodash";
import React, { useCallback, useMemo, useRef, useState } from "react";
import { Control, useFieldArray, useWatch } from "react-hook-form";
import { v4 } from "uuid";
import { TextInput } from "./TextInput";
import { TaskFormType } from "./types";

type TagsControlProps = {
  control: Control<TaskFormType>;
};

type TagOption = {
  id: string;
  name: string;
};

const TAGS_QUERY = gql(`
  query GetTaskTags($search: String, $page: Int, $pageSize: Int, $sort: [SortModel!]) {
    tags(search: $search, page: $page, pageSize: $pageSize, sort: $sort) {
      items {
        id
        name
      }
    }
  }
`);

export const TagsControl: React.FC<TagsControlProps> = ({ control }) => {
  const theme = useTheme();

  const { tenantId } = useTenant();
  const { data: tagsData } = useQuery(TAGS_QUERY, {
    variables: {
      search: `tenantId:${tenantId} type:${TagType.Task}`,
      sort: [{ field: "name", sort: "asc" }],
    },
  });

  // Map existing tags by lowercase name for quick lookup
  const existingTagsByLowerName = useMemo(() => {
    const all = tagsData?.tags?.items ?? [];
    const map = new Map<string, TagOption>();
    for (const t of all) {
      map.set(t.name.trim().toLowerCase(), { id: t.id, name: t.name });
    }
    return map;
  }, [tagsData?.tags?.items]);

  const allExistingTags = useMemo<TagOption[]>(
    () => tagsData?.tags?.items ?? [],
    [tagsData?.tags?.items]
  );

  const watchedTags = useWatch({ control, name: "tags" });
  const tags = useMemo(() => watchedTags ?? [], [watchedTags]);
  const { append, remove } = useFieldArray({
    control,
    name: "tags",
  });

  // State for adding mode
  const [isAdding, setIsAdding] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);
  const isAddingRef = useRef(isAdding);
  isAddingRef.current = isAdding;

  // Filter out already selected tags from options
  const selectedIds = useMemo(() => new Set(tags.map((t) => t.id)), [tags]);
  const availableOptions = useMemo(
    () => allExistingTags.filter((t) => !selectedIds.has(t.id)),
    [allExistingTags, selectedIds]
  );

  // Check if tag with name already exists in selection
  const hasTagWithName = useCallback(
    (name: string) =>
      tags.some(
        (t) => t.name.trim().toLowerCase() === name.trim().toLowerCase()
      ),
    [tags]
  );

  // Add a tag (either existing or new)
  const addTag = useCallback(
    (tag: TagOption | string) => {
      let tagToAdd: TagOption;

      if (typeof tag === "string") {
        const trimmed = tag.trim();
        if (!trimmed || hasTagWithName(trimmed)) {
          return;
        }
        // Check if it matches an existing tag (case-insensitive)
        const existing = existingTagsByLowerName.get(trimmed.toLowerCase());
        tagToAdd = existing ?? { id: v4(), name: trimmed };
      } else {
        if (hasTagWithName(tag.name)) {
          return;
        }
        tagToAdd = tag;
      }

      append(tagToAdd);
      setInputValue("");
      setIsAdding(false);
    },
    [append, hasTagWithName, existingTagsByLowerName]
  );

  const cancelAdd = useCallback(() => {
    setIsAdding(false);
    setInputValue("");
  }, []);

  const {
    getRootProps,
    getInputProps,
    getListboxProps,
    getOptionProps,
    groupedOptions,
  } = useAutocomplete({
    options: availableOptions,
    getOptionLabel: (option) =>
      typeof option === "string" ? option : option.name,
    freeSolo: true,
    inputValue,
    onInputChange: (_, value, reason) => {
      // Don't update input when selecting an option (it would set to the option's label)
      if (reason !== "reset") {
        setInputValue(value);
      }
    },
    onChange: (_, newValue) => {
      if (newValue) {
        addTag(newValue);
      }
    },
    filterOptions: (options, { inputValue }) => {
      const searchLower = inputValue.toLowerCase();
      return options.filter((opt) =>
        opt.name.toLowerCase().includes(searchLower)
      );
    },
    open: isAdding,
    isOptionEqualToValue: (option, value) => option.id === value.id,
  });

  const showDropdown = isAdding && groupedOptions.length > 0;

  // Handle input keydown for Escape/Backspace
  // Note: Enter is handled by useAutocomplete which calls onChange with the freeSolo value
  const handleInputKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Escape") {
        e.preventDefault();
        cancelAdd();
      } else if (e.key === "Backspace" && inputValue === "") {
        cancelAdd();
      }
    },
    [cancelAdd, inputValue]
  );

  const inputProps = getInputProps();

  return (
    <Stack
      direction="row"
      gap={theme.spacing(1)}
      flexWrap="wrap"
      alignItems="center"
    >
      {/* Selected tags as chips */}
      {tags.map((tag, index) => (
        <Chip key={tag.id} label={tag.name} onDelete={() => remove(index)} />
      ))}

      {/* Adding mode: input chip with dropdown */}
      {isAdding ? (
        <ClickAwayListener onClickAway={cancelAdd}>
          <Box {...getRootProps()} sx={{ position: "relative" }}>
            <Chip
              ref={setAnchorEl}
              label={
                <TextInput
                  inputRef={inputProps.ref}
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  onKeyDown={(e) => {
                    // Call useAutocomplete's onKeyDown first for arrow navigation
                    inputProps.onKeyDown?.(
                      e as React.KeyboardEvent<HTMLInputElement>
                    );
                    handleInputKeyDown(
                      e as React.KeyboardEvent<HTMLInputElement>
                    );
                  }}
                  onBlur={(e) => {
                    inputProps.onBlur?.(
                      e as React.FocusEvent<HTMLInputElement>
                    );
                    // Don't cancel if clicking on dropdown
                    if (!anchorEl?.contains(e.relatedTarget as Node)) {
                      // Delay to allow option click to register
                      // Check isAddingRef to avoid double-add if selection already happened
                      setTimeout(() => {
                        if (!isAddingRef.current) return;
                        if (inputValue.trim()) {
                          addTag(inputValue);
                        } else {
                          cancelAdd();
                        }
                      }, 150);
                    }
                  }}
                  autoFocus
                  placeholder="add tag..."
                  sx={{ minWidth: 80 }}
                />
              }
            />

            {/* Dropdown */}
            <Popper
              open={showDropdown && anchorEl !== null}
              anchorEl={anchorEl}
              placement="bottom-start"
              sx={{ zIndex: theme.zIndex.modal + 1 }}
            >
              <Paper
                elevation={8}
                sx={{
                  mt: 0.5,
                  maxHeight: 200,
                  overflow: "auto",
                  minWidth: 200,
                }}
              >
                <List {...getListboxProps()} dense disablePadding>
                  {(groupedOptions as TagOption[]).map((option, index) => {
                    const optionProps = getOptionProps({
                      option,
                      index,
                    });
                    return (
                      <ListItemButton
                        key={option.id}
                        {...omit(optionProps, "key")}
                        component="li"
                        sx={{
                          "&.Mui-focused": {
                            backgroundColor: theme.palette.action.hover,
                          },
                        }}
                      >
                        <ListItemText primary={option.name} />
                      </ListItemButton>
                    );
                  })}
                </List>
              </Paper>
            </Popper>
          </Box>
        </ClickAwayListener>
      ) : tags.length === 0 ? (
        <Chip label="add tag..." onClick={() => setIsAdding(true)} />
      ) : (
        <Tooltip title="Add tag" arrow>
          <Chip
            onClick={() => setIsAdding(true)}
            icon={<Add fontSize="small" />}
            slotProps={{
              root: { sx: { px: 0.5 } },
              label: { sx: { pl: 0 } },
            }}
          />
        </Tooltip>
      )}
    </Stack>
  );
};
